import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Mail, Phone, ChevronRight, ChevronDown, ChevronUp,
  MapPin, ArrowRight, ShieldCheck, Truck, Headphones,
  User, Building2, FileText, LifeBuoy, TrendingUp, LayoutGrid,
} from "lucide-react";
import { useStoreSettings } from "@/hooks/useStoreSettings";
import BrandLogo from "@/components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Social icon SVGs (used by both desktop + mobile) ─────────────────────────
const SOCIAL_ICONS = {
  linkedin:  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" /><circle cx="4" cy="4" r="2" /></svg>,
  facebook:  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" /></svg>,
  instagram: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><rect x="2" y="2" width="20" height="20" rx="5" /><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" fill="white" /></svg>,
  youtube:   <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 001.46 6.42 29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58z" /><polygon points="9.75,15.02 15.5,12 9.75,8.98 9.75,15.02" fill="white" /></svg>,
  twitter:   <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23 3a10.9 10.9 0 01-3.14 1.53A4.48 4.48 0 0022.43.36a9 9 0 01-2.88 1.1A4.52 4.52 0 0016.11 0c-2.5 0-4.52 2.02-4.52 4.5 0 .35.04.7.11 1.03C7.69 5.35 4.07 3.56 1.64.92a4.5 4.5 0 00-.61 2.27c0 1.56.8 2.94 2 3.75a4.47 4.47 0 01-2.05-.57v.06c0 2.18 1.55 4 3.61 4.41a4.52 4.52 0 01-2.04.08 4.52 4.52 0 004.22 3.13A9.05 9.05 0 010 19.54a12.77 12.77 0 006.92 2.03c8.3 0 12.84-6.88 12.84-12.84 0-.2 0-.4-.02-.59A9.16 9.16 0 0023 3z" /></svg>,
  whatsapp:  <Phone className="w-4 h-4" />,
  pinterest: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.08 3.16 9.43 7.63 11.17-.1-.97-.2-2.47.04-3.53.22-.95 1.46-6.2 1.46-6.2s-.37-.75-.37-1.85c0-1.74 1.01-3.04 2.26-3.04 1.07 0 1.59.8 1.59 1.76 0 1.07-.68 2.68-1.03 4.17-.29 1.24.62 2.25 1.84 2.25 2.2 0 3.9-2.32 3.9-5.67 0-2.96-2.13-5.03-5.17-5.03-3.52 0-5.59 2.64-5.59 5.37 0 1.06.41 2.2.92 2.82.1.12.11.23.08.35-.09.38-.3 1.24-.34 1.41-.05.22-.18.27-.4.16-1.5-.7-2.44-2.9-2.44-4.67 0-3.8 2.76-7.28 7.96-7.28 4.18 0 7.43 2.98 7.43 6.96 0 4.15-2.62 7.49-6.26 7.49-1.22 0-2.37-.64-2.77-1.38l-.75 2.82c-.27 1.05-1 2.36-1.49 3.16.97.3 2 .46 3.07.46 6.63 0 12-5.37 12-12S18.63 0 12 0z" /></svg>,
};

// Brand background per platform (used on mobile social icons)
const SOCIAL_BG = {
  instagram: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)",
  facebook:  "#1877F2",
  youtube:   "#FF0000",
  linkedin:  "#0A66C2",
  pinterest: "#E60023",
  twitter:   "#000000",
  whatsapp:  "#25D366",
  tiktok:    "#010101",
};

// Trust badges (static)
const TRUST_BADGES = [
  { Icon: ShieldCheck, label: "Secure\nPayments" },
  { Icon: Truck,       label: "Fast\nDelivery"  },
  { Icon: Headphones,  label: "Dedicated\nSupport" },
];

// Guess a Lucide icon from a column heading string
function colIcon(heading) {
  const h = (heading || "").toLowerCase();
  if (h.includes("customer") || h.includes("service")) return User;
  if (h.includes("company") || h.includes("about") || h.includes("brand")) return Building2;
  if (h.includes("polic") || h.includes("terms") || h.includes("legal")) return FileText;
  if (h.includes("help") || h.includes("support") || h.includes("faq")) return LifeBuoy;
  if (h.includes("trend") || h.includes("product") || h.includes("category")) return TrendingUp;
  return LayoutGrid;
}

// ── Mobile accordion row ──────────────────────────────────────────────────────
function MobileAccordion({ col }) {
  const [open, setOpen] = useState(false);
  if (!col?.title && !(col?.links?.length)) return null;
  const Icon = colIcon(col.title);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-4 bg-white"
      >
        <span
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: "var(--brand)14", color: "var(--brand)" }}
        >
          <Icon className="w-4 h-4" />
        </span>
        <span className="flex-1 text-sm font-bold text-slate-800 text-left">{col.title}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pl-[3.25rem] space-y-2.5 bg-white">
          {(col.links || []).map((l, i) => (
            <Link
              key={i}
              to={l.url || "/"}
              className="block text-sm text-slate-500 hover:text-orange-500 transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export default function SiteFooter({ configProp = null }) {
  const [config, setConfig] = useState(configProp);
  const [nlEmail, setNlEmail] = useState("");
  const [nlMsg,   setNlMsg]   = useState("");
  const storeSettings = useStoreSettings();
  const storeName = storeSettings?.store_name || "Our Store";

  useEffect(() => {
    if (!configProp) {
      axios.get(`${API}/homepage`)
        .then(r => setConfig(r.data?.footer || null))
        .catch(() => {});
    }
  }, [configProp]);

  if (!config) return (
    <div className="bg-[#f3f4f6] border-t border-slate-200 py-6 text-center text-xs text-slate-400">
      © {new Date().getFullYear()} {storeName}. All rights reserved.
    </div>
  );

  const col1   = config.links_col1 || {};
  const col2   = config.links_col2 || {};
  const col3   = config.links_col3 || {};
  const phones = config.phones || [];

  // Social: new accounts-array format + legacy flat fallback
  const rawSocial = config.social || {};
  const socialAccounts = (() => {
    if (Array.isArray(rawSocial.accounts)) return rawSocial.accounts;
    return ["linkedin", "facebook", "instagram", "youtube"].reduce((acc, p) => {
      if (rawSocial[p]) acc.push({ platform: p, url: rawSocial[p], icon_url: "", label: "" });
      return acc;
    }, []);
  })();
  const followUsTitle = rawSocial.follow_us_title || "Follow Us";
  const activeSocials = socialAccounts.filter(a => a.url);

  const why     = config.why_section     || {};
  const qs      = config.quick_searches  || {};
  const al      = config.asset_lite_section || {};
  const story   = config.story_section   || {};
  const contact = config.contact_section || {};
  const seo     = config.seo_section     || {};

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!nlEmail) return;
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: nlEmail });
      setNlMsg("Subscribed! Thank you.");
      setNlEmail("");
    } catch {
      setNlMsg("Already subscribed or error.");
    }
    setTimeout(() => setNlMsg(""), 4000);
  };

  const year = new Date().getFullYear();

  return (
    <>
      {/* ══════════════════════════════════════════════════════════════
          DESKTOP FOOTER  (hidden on mobile)
      ══════════════════════════════════════════════════════════════ */}
      <footer data-testid="site-footer" className="hidden md:block">

        {/* ── SECTION 1: MAIN FOOTER COLUMNS ── */}
        <div className="bg-[#f3f4f6] border-t border-slate-200">
          <div className="max-w-[1600px] mx-auto px-6 py-10">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-8">

              {/* Brand column */}
              <div className="col-span-2 md:col-span-1">
                <Link to="/" className="inline-block mb-3">
                  {(config.logo_display_mode === "image" || config.logo_display_mode === "both") && config.footer_logo_url && (
                    <img src={config.footer_logo_url} alt={config.logo_text || "Logo"}
                      style={{ height: `${config.footer_logo_height || 32}px`, maxWidth: "180px", objectFit: "contain", display: "block" }}
                      className="mb-1" />
                  )}
                  {(!config.logo_display_mode || config.logo_display_mode === "text" || config.logo_display_mode === "both" || !config.footer_logo_url) && (
                    <span className="text-2xl font-black tracking-tight" style={{ color: "var(--brand)" }}>
                      {config.logo_text || "importrend"}
                      <span className="text-sm font-semibold" style={{ color: "var(--brand)", opacity: 0.7 }}>.com</span>
                    </span>
                  )}
                </Link>
                <p className="text-xs text-slate-500 leading-relaxed mb-3">{config.tagline}</p>
                {config.view_more_url && (
                  <Link to={config.view_more_url} className="text-sm font-semibold flex items-center gap-1 hover:underline" style={{ color: "var(--brand)" }}>
                    {config.view_more_text || "View More"} <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </div>

              {/* Connect With Us */}
              <div>
                <p className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-4">CONNECT WITH US</p>
                {config.office_timings && (
                  <p className="text-xs text-slate-500 mb-4 whitespace-pre-line">{config.office_timings}</p>
                )}
                {config.email && (
                  <a href={`mailto:${config.email}`} className="flex items-center gap-2.5 mb-2 group">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--brand)" }}>
                      <Mail className="w-3.5 h-3.5 text-white" />
                    </div>
                    <span className="text-xs font-semibold group-hover:underline" style={{ color: "var(--brand)" }}>{config.email}</span>
                  </a>
                )}
                {phones.map((p, i) => (
                  <a key={i} href={`tel:${p.number}`} className="flex items-center gap-2.5 mb-2 group">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--brand)" }}>
                      <Phone className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div>
                      {p.label && <span className="text-[10px] text-slate-400 font-semibold block">{p.label}</span>}
                      <span className="text-xs font-semibold group-hover:underline" style={{ color: "var(--brand)" }}>{p.number}</span>
                    </div>
                  </a>
                ))}
              </div>

              {/* Nav columns */}
              {[col1, col2, col3].map((col, i) => (
                <div key={i}>
                  <p className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-4">{col.title}</p>
                  <ul className="space-y-2.5">
                    {(col.links || []).map((link, j) => (
                      <li key={j}>
                        <Link to={link.url || "/"} className={`text-xs transition-colors ${link.highlight ? "font-semibold hover:underline" : "text-slate-500 hover:text-slate-800"}`}
                          style={link.highlight ? { color: "var(--brand)" } : {}}>
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Social icons */}
            {activeSocials.length > 0 && (
              <div className="mt-8">
                {followUsTitle && (
                  <p className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3">{followUsTitle}</p>
                )}
                <div className="flex flex-wrap gap-2">
                  {activeSocials.map((acc, i) => (
                    <a key={i} href={acc.url} target="_blank" rel="noreferrer" title={acc.label || acc.platform}
                      className="w-9 h-9 rounded-lg flex items-center justify-center transition-all hover:opacity-80"
                      style={acc.icon_url
                        ? { backgroundColor: "#ffffff", border: "1px solid #e5e7eb" }
                        : { backgroundColor: "var(--brand)" }}>
                      {acc.icon_url
                        ? <img src={acc.icon_url} alt={acc.label || acc.platform} className="w-6 h-6 object-contain" />
                        : (SOCIAL_ICONS[acc.platform] || <span className="text-xs font-bold uppercase text-white">{(acc.platform || "?")[0]}</span>)
                      }
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Copyright bar */}
          <div className="border-t border-slate-200 px-6 py-4">
            <div className="max-w-[1600px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-xs text-slate-400">
                {config.copyright || `© ${year} Importrend. All rights reserved.`}
              </p>
              {config.show_payment_icons && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-400 mr-1">Our Partners</span>
                  {["Amazon", "Shopify", "RuPay", "VISA", "Maestro", "UPI"].map(p => (
                    <span key={p} className="bg-white border border-slate-200 text-slate-500 text-[9px] font-bold px-2 py-1 rounded">{p}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── SECTION 2: WHY + QUICK SEARCHES + ASSET-LITE ── */}
        <div className="bg-white border-t border-slate-100">
          {why.enabled && why.items?.length > 0 && (
            <div className="max-w-[1600px] mx-auto px-6 py-10 border-b border-slate-100">
              {why.title && <p className="text-xs font-bold text-slate-800 uppercase tracking-widest mb-1">{why.title}</p>}
              {why.description && <p className="text-sm text-slate-500 mb-6">{why.description}</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {why.items.map((item, i) => (
                  <div key={i} className="border border-slate-200 rounded-lg p-4">
                    <p className="text-sm font-bold text-slate-800 mb-1">{item.title}</p>
                    <p className="text-xs text-slate-500 leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {qs.enabled && qs.groups?.length > 0 && (
            <div className="max-w-[1600px] mx-auto px-6 py-10 border-b border-slate-100">
              <p className="text-xs font-bold text-slate-800 uppercase tracking-widest mb-5">QUICK SEARCHES</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {qs.groups.map((group, i) => (
                  <div key={i}>
                    <p className="text-sm font-bold text-slate-700 mb-3">{group.title}</p>
                    <div className="flex flex-wrap gap-2">
                      {(group.tags || []).map((tag, j) => (
                        <Link key={j} to={`/search?q=${encodeURIComponent(tag)}`}
                          className="text-xs border border-slate-300 rounded px-3 py-1.5 text-slate-600 transition-colors hover:border-current"
                          onMouseEnter={e => { e.currentTarget.style.color = "var(--brand)"; e.currentTarget.style.borderColor = "var(--brand)"; }}
                          onMouseLeave={e => { e.currentTarget.style.color = ""; e.currentTarget.style.borderColor = ""; }}
                        >{tag}</Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {al.enabled && (
            <div className="max-w-[1600px] mx-auto px-6 py-10 border-b border-slate-100">
              {al.title && <p className="text-xs font-bold text-slate-800 uppercase tracking-widest mb-1">{al.title}</p>}
              {al.description && <p className="text-sm text-slate-500 mb-6">{al.description}</p>}
              {al.items?.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {al.items.map((item, i) => (
                    <div key={i} className="border border-slate-200 rounded-lg p-4">
                      <p className="text-xs text-slate-600 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── SECTION 3: STORY + CONTACT ── */}
        <div className="bg-white border-t border-slate-100">
          {story.enabled && story.paragraphs?.length > 0 && (
            <div className="max-w-[1600px] mx-auto px-6 py-10 border-b border-slate-100">
              {story.title && <p className="text-xs font-bold text-slate-800 uppercase tracking-widest mb-5">{story.title}</p>}
              <div className="space-y-3 max-w-3xl">
                {story.paragraphs.map((para, i) => <p key={i} className="text-xs text-slate-500 leading-relaxed">{para}</p>)}
              </div>
            </div>
          )}
          {contact.enabled && (
            <div className="max-w-[1600px] mx-auto px-6 py-10 border-b border-slate-100">
              {contact.title && <p className="text-xs font-bold text-slate-800 uppercase tracking-widest mb-1">{contact.title}</p>}
              {contact.description && <p className="text-sm text-slate-500 mb-5">{contact.description}</p>}
              {contact.items?.length > 0 && (
                <div className="border border-slate-200 rounded-lg p-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2">
                    {contact.items.map((item, i) => (
                      <p key={i} className="text-xs text-slate-600">
                        <span className="font-bold text-slate-800">{item.label}:</span> {item.value}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── SEO TEXT ── */}
        {seo.enabled && seo.paragraphs?.length > 0 && (
          <div className="bg-white border-t border-slate-100 px-6 py-8">
            <div className="max-w-[1600px] mx-auto">
              {seo.title && <p className="text-xs font-bold text-slate-700 uppercase tracking-widest mb-3">{seo.title}</p>}
              <div className="space-y-3">
                {seo.paragraphs.map((p, i) => <p key={i} className="text-xs text-slate-500 leading-relaxed">{p}</p>)}
              </div>
            </div>
          </div>
        )}
      </footer>

      {/* ══════════════════════════════════════════════════════════════
          MOBILE FOOTER  (hidden on desktop)
      ══════════════════════════════════════════════════════════════ */}
      <div className="md:hidden" data-testid="site-footer-mobile">

        {/* ── 1. Hero: Logo + tagline + newsletter ── */}
        <div className="bg-white px-4 pt-6 pb-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              {/* Logo */}
              <Link to="/">
                {(config.logo_display_mode === "image" || config.logo_display_mode === "both") && config.footer_logo_url ? (
                  <img src={config.footer_logo_url} alt={config.logo_text || "Logo"}
                    style={{ height: `${config.footer_logo_height || 36}px`, maxWidth: "160px", objectFit: "contain" }}
                    className="mb-1 block" />
                ) : (
                  <div className="mb-1">
                    <BrandLogo size="lg" />
                    <p className="text-[9px] font-bold text-slate-400 tracking-[0.18em] uppercase -mt-0.5">Global Deals</p>
                  </div>
                )}
              </Link>
              <p className="text-xs text-slate-500 leading-snug mt-1 max-w-[200px]">
                {config.tagline || "Quality products for home, lifestyle and personal care."}
              </p>
            </div>

            {/* Decorative brand circle */}
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center flex-shrink-0 opacity-90"
              style={{ background: `radial-gradient(circle at 60% 40%, var(--brand), #ff9a5a)` }}
            >
              <svg viewBox="0 0 80 80" fill="none" className="w-14 h-14">
                {/* globe lines */}
                <circle cx="40" cy="40" r="26" stroke="white" strokeWidth="1.5" strokeOpacity="0.6" />
                <ellipse cx="40" cy="40" rx="12" ry="26" stroke="white" strokeWidth="1.2" strokeOpacity="0.5" />
                <line x1="14" y1="40" x2="66" y2="40" stroke="white" strokeWidth="1.2" strokeOpacity="0.5" />
                {/* plane */}
                <path d="M18 28 L30 22 L28 30 Z" fill="white" />
                {/* location pin */}
                <circle cx="58" cy="35" r="3.5" fill="white" />
                <path d="M58 38.5 L58 44" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
          </div>

          {/* Newsletter */}
          <div className="mt-5">
            <p className="text-base font-bold text-slate-900 mb-0.5">Stay Updated</p>
            <p className="text-xs text-slate-400 mb-3">Get the latest deals, new arrivals and offers.</p>
            <form onSubmit={handleNewsletter} className="flex gap-2">
              <div className="flex-1 flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2.5">
                <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={nlEmail}
                  onChange={e => setNlEmail(e.target.value)}
                  className="flex-1 text-sm bg-transparent outline-none text-slate-700 placeholder-slate-300 min-w-0"
                />
              </div>
              <button type="submit"
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-opacity hover:opacity-90"
                style={{ backgroundColor: "var(--brand)" }}>
                <ArrowRight className="w-5 h-5 text-white" />
              </button>
            </form>
            {nlMsg && <p className="text-xs mt-1.5" style={{ color: "var(--brand)" }}>{nlMsg}</p>}
          </div>
        </div>

        {/* ── 2. Connect With Us ── */}
        <div className="px-4 py-5" style={{ backgroundColor: "var(--brand)0d" }}>
          <p className="text-base font-bold text-slate-900 mb-4">Connect With Us</p>
          <div className="grid grid-cols-3 gap-3">
            {/* Call Us */}
            <div className="flex flex-col items-center text-center gap-1.5">
              <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-sm"
                style={{ backgroundColor: "var(--brand)" }}>
                <Phone className="w-5 h-5 text-white" />
              </div>
              <p className="text-xs font-bold text-slate-800">Call Us</p>
              {phones[0] ? (
                <>
                  <a href={`tel:${phones[0].number}`} className="text-[10px] font-bold leading-tight" style={{ color: "var(--brand)" }}>
                    {phones[0].number}
                  </a>
                  <p className="text-[9px] text-slate-400 leading-tight">
                    {config.office_timings || "Mon – Sat, 9:00 AM to 6:00 PM"}
                  </p>
                </>
              ) : (
                <p className="text-[9px] text-slate-400 leading-tight">Mon – Sat, 9:00 AM to 6:00 PM</p>
              )}
            </div>

            {/* Email Us */}
            <div className="flex flex-col items-center text-center gap-1.5 border-x border-orange-100 px-1">
              <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-sm"
                style={{ backgroundColor: "var(--brand)" }}>
                <Mail className="w-5 h-5 text-white" />
              </div>
              <p className="text-xs font-bold text-slate-800">Email Us</p>
              <a href={`mailto:${config.email || "contact@importrend.com"}`}
                className="text-[10px] font-bold leading-tight break-all"
                style={{ color: "var(--brand)" }}>
                {config.email || "contact@importrend.com"}
              </a>
              <p className="text-[9px] text-slate-400 leading-tight">We reply within 24 hours</p>
            </div>

            {/* Our Office */}
            <div className="flex flex-col items-center text-center gap-1.5">
              <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-sm"
                style={{ backgroundColor: "var(--brand)" }}>
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <p className="text-xs font-bold text-slate-800">Our Office</p>
              <p className="text-[9px] text-slate-400 leading-tight">
                {config.address || "79/3, 2nd Floor, Benson Town, Bengaluru – 560046"}
              </p>
            </div>
          </div>
        </div>

        {/* ── 3. Accordion link sections ── */}
        <div className="bg-white">
          {[col1, col2, col3].map((col, i) => (
            <MobileAccordion key={i} col={col} />
          ))}
        </div>

        {/* ── 4. Follow Us ── */}
        {activeSocials.length > 0 && (
          <div className="bg-white border-t border-slate-100 px-4 pt-5 pb-6">
            <p className="text-base font-bold text-slate-900 mb-0.5">{followUsTitle}</p>
            <p className="text-xs text-slate-400 mb-4">Stay connected for new arrivals and exciting offers!</p>
            <div className="flex flex-wrap gap-3">
              {activeSocials.map((acc, i) => (
                <a key={i} href={acc.url} target="_blank" rel="noreferrer"
                  title={acc.label || acc.platform}
                  className="w-11 h-11 rounded-full flex items-center justify-center transition-transform hover:scale-110 shadow-sm"
                  style={acc.icon_url
                    ? { backgroundColor: "#ffffff", border: "1px solid #e5e7eb" }
                    : { background: SOCIAL_BG[acc.platform] || "var(--brand)" }}>
                  {acc.icon_url
                    ? <img src={acc.icon_url} alt={acc.platform} className="w-6 h-6 object-contain" />
                    : (SOCIAL_ICONS[acc.platform]
                        ? <span className="text-white [&>svg]:w-5 [&>svg]:h-5">{SOCIAL_ICONS[acc.platform]}</span>
                        : <span className="text-sm font-bold text-white uppercase">{(acc.platform||"?")[0]}</span>
                      )
                  }
                </a>
              ))}
            </div>
          </div>
        )}

        {/* ── 5. Trust badges ── */}
        <div className="bg-white border-t border-slate-100 px-4 py-4">
          <div className="grid grid-cols-3 divide-x divide-slate-200">
            {TRUST_BADGES.map((b, i) => (
              <div key={i} className="flex items-center gap-2 px-2 first:pl-0 last:pr-0">
                <b.Icon className="w-7 h-7 text-slate-700 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-[11px] font-bold text-slate-700 leading-tight whitespace-pre-line">{b.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── 6. Wave + scroll-to-top + copyright ── */}
        <div className="relative overflow-hidden">
          {/* Three-layer wave */}
          <svg viewBox="0 0 375 72" preserveAspectRatio="none" className="w-full block" style={{ marginBottom: "-1px" }}>
            <path d="M0,48 Q93.75,8 187.5,48 Q281.25,88 375,48 L375,72 L0,72 Z"
              style={{ fill: "var(--brand)", opacity: 0.25 }} />
            <path d="M0,56 Q93.75,20 187.5,56 Q281.25,92 375,56 L375,72 L0,72 Z"
              style={{ fill: "var(--brand)", opacity: 0.5 }} />
            <path d="M0,62 Q93.75,30 187.5,62 Q281.25,94 375,62 L375,72 L0,72 Z"
              style={{ fill: "var(--brand)" }} />
          </svg>

          {/* Scroll-to-top button – floats on the wave */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg border-4 border-white"
              style={{ backgroundColor: "var(--brand)" }}
              aria-label="Scroll to top"
            >
              <ChevronUp className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Copyright strip */}
          <div className="py-4 text-center" style={{ backgroundColor: "var(--brand)" }}>
            <p className="text-xs text-white/90">
              {config.copyright || `© ${year} ${storeName}. All Rights Reserved.`}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
